import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth } from "@/integrations/firebase/client";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, User, Menu, Film, Camera, Palette } from "lucide-react";
import { useAdmin } from "@/hooks/use-admin";
import type { Firestore } from "firebase/firestore";
import { doc, onSnapshot } from "firebase/firestore";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

const Navbar = () => {
  const [ready, setReady] = useState(false);
  const [isAuthed, setIsAuthed] = useState<boolean>(!!auth.currentUser);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setIsAuthed(!!u);
      setReady(true);
    });
    return () => unsub();
  }, []);

  const { isAdmin } = useAdmin();
  const [credits, setCredits] = useState<number | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [creditsFlash, setCreditsFlash] = useState<"up" | "down" | null>(null);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let dbRef: Firestore | undefined;
    (async () => {
      if (!auth.currentUser) { setCredits(null); return; }
      const mod = await import("@/integrations/firebase/client");
      dbRef = mod.db;
      const ref = doc(dbRef, 'users', auth.currentUser.uid);
      unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() as any;
      const newCredits = typeof data?.credits === 'number' ? data.credits : null;
      const newTier = typeof data?.subscription_tier === 'string' ? data.subscription_tier : 'free';
      setCredits((prev) => {
        if (typeof prev === 'number' && typeof newCredits === 'number' && prev !== newCredits) {
          setCreditsFlash(newCredits > prev ? 'up' : 'down');
          window.setTimeout(() => setCreditsFlash(null), 700);
        }
        return newCredits;
      });
      setTier(newTier);
      });
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [isAuthed]);

  const isSubscriptionActive = (() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const tab = String(params.get('tab') || '').toLowerCase();
      const hash = String(location.hash || '').toLowerCase();
      return location.pathname === '/account' && (tab === 'subscription' || hash.includes('subscription'));
    } catch {
      return location.pathname === '/account';
    }
  })();

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-2 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src="/Beemm_enhancer_logo.png" 
            srcSet="/Beemm_enhancer_logo.png 1x, /Beemm_enhancer_logo@2x.png 2x, /Beemm_enhancer_logo@4x.png 4x"
            alt="Beemm Enhancer" 
            className="h-9 w-9 object-contain bg-transparent filter invert" 
          />
          <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Ronda, sans-serif' }}>
            beemm enhancer
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {/* Desktop/Tablet navigation */}
          <nav className="hidden md:flex items-center gap-2">
            <Button asChild variant={location.pathname === "/editor" ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/editor">Editor</Link>
            </Button>
            <Button asChild variant={location.pathname === "/gallery" ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/gallery">Gallery</Link>
            </Button>
            <Button asChild variant={location.pathname === "/storyboard" ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/storyboard" className="flex items-center gap-1">
                <Film className="h-4 w-4" />
                StoryBoard
              </Link>
            </Button>
            <Button asChild variant={location.pathname === "/perspective" ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/perspective" className="flex items-center gap-1">
                <Camera className="h-4 w-4" />
                Perspective
              </Link>
            </Button>
            <Button asChild variant={location.pathname === "/colorgrading" ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/colorgrading" className="flex items-center gap-1">
                <Palette className="h-4 w-4" />
                Color Grading
              </Link>
            </Button>
            <Button asChild variant={isSubscriptionActive ? "secondary" : "ghost"} className="text-muted-foreground hover:text-foreground">
              <Link to="/account?tab=subscription">Abonnement</Link>
            </Button>

            {!isAuthed ? (
              <Button asChild variant="default">
                <Link to="/auth">Se connecter</Link>
              </Button>
            ) : (
              <>
                {isAdmin ? (
                  <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
                    <Link to="/admin">Admin</Link>
                  </Button>
                ) : null}
                {typeof credits === 'number' ? (
                  <div
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs transition-all duration-300",
                      creditsFlash === 'up' && "bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500 scale-110 border-emerald-500/50",
                      creditsFlash === 'down' && "bg-destructive/10 text-destructive ring-1 ring-destructive scale-110 border-destructive/50",
                      !creditsFlash && "border-border text-muted-foreground"
                    )}
                  >
                    {credits} crédits
                  </div>
                ) : null}
                {tier ? (
                  <div className={cn(
                    "rounded-full border px-2 py-1 text-xs capitalize",
                    tier === 'pro' ? "border-purple-500/50 text-purple-500" : tier === 'classic' ? "border-primary/50 text-primary" : "border-border text-muted-foreground"
                  )}>
                    {tier}
                  </div>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={auth.currentUser?.photoURL || undefined} alt={auth.currentUser?.displayName || auth.currentUser?.email || auth.currentUser?.uid || "User"} />
                        <AvatarFallback>
                          {(auth.currentUser?.displayName?.[0] || auth.currentUser?.email?.[0] || "U").toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block text-sm">
                        {auth.currentUser?.displayName || auth.currentUser?.email || auth.currentUser?.uid}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-sm">Mon compte</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/account')}>
                      Paramètres
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" />
                        <span>{auth.currentUser?.displayName || "Utilisateur"}</span>
                      </div>
                      {auth.currentUser?.email ? <div className="truncate">{auth.currentUser?.email}</div> : null}
                      {auth.currentUser?.uid ? <div className="truncate">UID: {auth.currentUser?.uid}</div> : null}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          await signOut(auth);
                          navigate("/auth");
                        } catch {}
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>

          {/* Mobile hamburger menu */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Ouvrir le menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm">
                <div className="p-4 space-y-4">
                  {isAuthed ? (
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={auth.currentUser?.photoURL || undefined} />
                        <AvatarFallback>{(auth.currentUser?.displayName?.[0] || auth.currentUser?.email?.[0] || 'U').toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{auth.currentUser?.displayName || auth.currentUser?.email || auth.currentUser?.uid}</div>
                        <div className="flex items-center gap-2 mt-1">
                          {typeof credits === 'number' ? (
                            <div className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">{credits} crédits</div>
                          ) : null}
                          {tier ? (
                            <div className={cn(
                              "rounded-full border px-2 py-0.5 text-[11px] capitalize",
                              tier === 'pro' ? "border-purple-500/50 text-purple-500" : tier === 'classic' ? "border-primary/50 text-primary" : "border-border text-muted-foreground"
                            )}>{tier}</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    <SheetClose asChild>
                      <Button asChild variant={location.pathname === "/editor" ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/editor">Editor</Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild variant={location.pathname === "/gallery" ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/gallery">Gallery</Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild variant={location.pathname === "/storyboard" ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/storyboard" className="flex items-center gap-2">
                          <Film className="h-4 w-4" />
                          StoryBoard
                        </Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild variant={location.pathname === "/perspective" ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/perspective" className="flex items-center gap-2">
                          <Camera className="h-4 w-4" />
                          Perspective
                        </Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild variant={location.pathname === "/colorgrading" ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/colorgrading" className="flex items-center gap-2">
                          <Palette className="h-4 w-4" />
                          Color Grading
                        </Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button asChild variant={isSubscriptionActive ? "secondary" : "ghost"} className="w-full justify-start">
                        <Link to="/account?tab=subscription">Abonnement</Link>
                      </Button>
                    </SheetClose>
                    {isAdmin ? (
                      <SheetClose asChild>
                        <Button asChild variant={location.pathname === "/admin" ? "secondary" : "ghost"} className="w-full justify-start">
                          <Link to="/admin">Admin</Link>
                        </Button>
                      </SheetClose>
                    ) : null}
                  </div>

                  <Separator />

                  {!isAuthed ? (
                    <SheetClose asChild>
                      <Button asChild className="w-full">
                        <Link to="/auth">Se connecter</Link>
                      </Button>
                    </SheetClose>
                  ) : (
                    <div className="space-y-2">
                      <SheetClose asChild>
                        <Button asChild variant="ghost" className="w-full justify-start" onClick={() => navigate('/account')}>
                          <Link to="/account">Mon compte</Link>
                        </Button>
                      </SheetClose>
                      <Button
                        variant="destructive"
                        className="w-full justify-start"
                        onClick={async () => {
                          try {
                            await signOut(auth);
                            navigate("/auth");
                          } catch {}
                        }}
                      >
                        Se déconnecter
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;


